import { useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Code,
  Container,
  CopyButton,
  FileInput,
  Group,
  Modal,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconLock, IconRefresh, IconTrash, IconUpload } from "@tabler/icons-react";
import { AppearanceSettingsTab } from "../components/settings/AppearanceSettingsTab";
import { ScannerSettingsTab } from "../components/settings/ScannerSettingsTab";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import { AppLogo } from "../components/AppLogo";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";
import { NotificationsSettingsTab } from "../components/settings/NotificationsSettingsTab";
import { PublicSearchSettingsTab } from "../components/settings/PublicSearchSettingsTab";
import { ShipmentsSettingsTab } from "../components/settings/ShipmentsSettingsTab";
import { TelegramGroupsTab } from "../components/settings/TelegramGroupsTab";
import { FreeIssuesSettingsTab } from "../components/settings/FreeIssuesSettingsTab";
import { useBranding } from "../contexts/BrandingContext";
import type { ApiTokenStatus, CurrentUser, Role, User } from "../api/types";

interface SettingsProps {
  user: CurrentUser;
  onUserUpdate: (u: CurrentUser) => void;
}

const ROLE_OPTIONS = [
  { value: "admin", label: "admin" },
  { value: "operator", label: "operator" },
  { value: "viewer", label: "viewer" },
];

const MAX_LOGO_MB = 2;

export function Settings({ user, onUserUpdate }: SettingsProps) {
  const { t } = useTranslation();
  const isAdmin = user.role === "admin";
  const [tab, setTab] = useState<string | null>("profile");

  return (
    <Container size="md">
      <Title order={2} mb="md">{t("settings.title")}</Title>
      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="profile">{t("settings.tabProfile")}</Tabs.Tab>
          <Tabs.Tab value="appearance">{t("settings.tabAppearance")}</Tabs.Tab>
          <Tabs.Tab value="scanner">{t("settings.tabScanner")}</Tabs.Tab>
          <Tabs.Tab value="notifications">{t("settings.tabNotifications")}</Tabs.Tab>
          {isAdmin && <Tabs.Tab value="users">{t("settings.tabUsers")}</Tabs.Tab>}
          {isAdmin && <Tabs.Tab value="branding">{t("settings.tabBranding")}</Tabs.Tab>}
          {isAdmin && <Tabs.Tab value="shipments">{t("settings.tabShipments")}</Tabs.Tab>}
          {isAdmin && <Tabs.Tab value="publicSearch">{t("settings.tabPublicSearch")}</Tabs.Tab>}
          {isAdmin && <Tabs.Tab value="telegramGroups">{t("settings.tabTelegramGroups")}</Tabs.Tab>}
          {isAdmin && <Tabs.Tab value="freeIssues">{t("settings.tabFreeIssues")}</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="profile" pt="md">
          <ProfileTab user={user} onUserUpdate={onUserUpdate} />
        </Tabs.Panel>
        <Tabs.Panel value="appearance" pt="md">
          <AppearanceSettingsTab />
        </Tabs.Panel>
        <Tabs.Panel value="scanner" pt="md">
          {/* Mounted only while open so the test camera never runs in the background. */}
          {tab === "scanner" && <ScannerSettingsTab />}
        </Tabs.Panel>
        <Tabs.Panel value="notifications" pt="md">
          <NotificationsSettingsTab />
        </Tabs.Panel>
        {isAdmin && (
          <Tabs.Panel value="users" pt="md">
            <UsersTab currentUser={user} />
          </Tabs.Panel>
        )}
        {isAdmin && (
          <Tabs.Panel value="branding" pt="md">
            <BrandingTab />
            {isAdmin && (
              <Card withBorder mt="md">
                <Title order={5} mb="xs">{t("settings.adminTelegram.title")}</Title>
                <AdminTelegramCard />
              </Card>
            )}
          </Tabs.Panel>
        )}
        {isAdmin && (
          <Tabs.Panel value="shipments" pt="md">
            <ShipmentsSettingsTab />
          </Tabs.Panel>
        )}
        {isAdmin && (
          <Tabs.Panel value="publicSearch" pt="md">
            <PublicSearchSettingsTab />
          </Tabs.Panel>
        )}
        {isAdmin && (
          <Tabs.Panel value="telegramGroups" pt="md">
            <TelegramGroupsTab />
          </Tabs.Panel>
        )}
        {isAdmin && (
          <Tabs.Panel value="freeIssues" pt="md">
            <FreeIssuesSettingsTab />
          </Tabs.Panel>
        )}
      </Tabs>
    </Container>
  );
}

// ── Track17 section ──────────────────────────────────────────────────────────

function Track17Section() {
  const [isSet, setIsSet] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  async function loadStatus() {
    try {
      const r = await api.getSetting("track17.api_key");
      setIsSet(r.set);
    } catch {
      // soft-fail
    }
  }

  useEffect(() => { void loadStatus(); }, []);

  async function save() {
    if (!value.trim()) return;
    setSaving(true);
    try {
      await api.putSetting("track17.api_key", value.trim());
      setValue("");
      await loadStatus();
      notifications.show({ color: "teal", message: "17track key saved" });
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    setTesting(true);
    try {
      const r = await api.testTrack17Key();
      if (r.ok) {
        notifications.show({ color: "teal", message: "17track key OK" });
      } else {
        notifications.show({ color: "red", message: r.error ?? "Test failed" });
      }
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setTesting(false);
    }
  }

  return (
    <Card withBorder>
      <Group justify="space-between" mb="xs">
        <Title order={5}>17track</Title>
        {isSet ? <Badge color="teal">configured ✓</Badge> : <Badge color="gray">not set</Badge>}
      </Group>
      <Stack>
        <PasswordInput
          label="API key"
          placeholder="paste your 17track API key"
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
        />
        <Group>
          <Button onClick={() => void save()} loading={saving} disabled={!value.trim()}>
            Save
          </Button>
          <Button variant="default" onClick={() => void test()} loading={testing} disabled={!isSet}>
            Test
          </Button>
        </Group>
        <Text size="xs" c="dimmed">
          The key is stored on the server; never returned in API responses.
        </Text>
      </Stack>
    </Card>
  );
}

// ── Profile tab ──────────────────────────────────────────────────────────────

function ProfileTab({ user, onUserUpdate }: { user: CurrentUser; onUserUpdate: (u: CurrentUser) => void }) {
  const { t } = useTranslation();
  const emailForm = useForm({ initialValues: { email: user.email } });
  const pwdForm = useForm({
    initialValues: { current: "", new1: "", new2: "" },
    validate: {
      current: (v) => (v.length === 0 ? t("settings.currentPasswordRequired") : null),
      new1: (v) => (v.length < 8 ? t("settings.pwdMin", { n: 8 }) : null),
      new2: (v, vals) => (v !== vals.new1 ? t("settings.pwdMismatch") : null),
    },
  });

  async function saveEmail(values: { email: string }) {
    try {
      const u = await api.updateMyProfile({ email: values.email.trim() });
      onUserUpdate(u);
      notifications.show({ color: "green", message: t("settings.emailSaved") });
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      const msg =
        detail && typeof detail === "object" && "error" in detail
          ? String((detail as Record<string, unknown>).error)
          : t("settings.saveFailed");
      notifications.show({ color: "red", message: msg });
    }
  }

  async function savePassword(values: { current: string; new1: string; new2: string }) {
    try {
      await api.changeMyPassword(values.current, values.new1);
      pwdForm.reset();
      notifications.show({ color: "green", message: t("settings.pwdSaved") });
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      const msg =
        detail && typeof detail === "object" && "error" in detail
          ? String((detail as Record<string, unknown>).error)
          : t("settings.saveFailed");
      notifications.show({
        color: "red",
        message: msg === "wrong_current_password" ? t("settings.wrongCurrentPassword") : msg,
      });
    }
  }

  return (
    <Stack gap="lg">
      <Paper withBorder p="md">
        <form onSubmit={emailForm.onSubmit(saveEmail)}>
          <Stack gap="sm">
            <TextInput label={t("settings.usernameLabel")} value={user.username} disabled />
            <TextInput
              label={t("settings.emailLabel")}
              type="email"
              required
              {...emailForm.getInputProps("email")}
            />
            <Group justify="flex-end">
              <Button type="submit">{t("common.save")}</Button>
            </Group>
          </Stack>
        </form>
      </Paper>

      <Paper withBorder p="md">
        <form onSubmit={pwdForm.onSubmit(savePassword)}>
          <Stack gap="sm">
            <Title order={5}>{t("settings.changePasswordTitle")}</Title>
            <PasswordInput
              label={t("settings.currentPasswordLabel")}
              autoComplete="current-password"
              {...pwdForm.getInputProps("current")}
            />
            <PasswordInput
              label={t("settings.newPasswordLabel")}
              {...pwdForm.getInputProps("new1")}
            />
            <PasswordInput
              label={t("settings.repeatPasswordLabel")}
              {...pwdForm.getInputProps("new2")}
            />
            <Group justify="flex-end">
              <Button type="submit" leftSection={<IconLock size={14} />}>
                {t("settings.savePasswordButton")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>

      <LocalAgentTokenCard />

      <Track17Section />
    </Stack>
  );
}

// ── Users tab ────────────────────────────────────────────────────────────────

function UsersTab({ currentUser }: { currentUser: CurrentUser }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      setUsers(await api.listUsers());
    } catch {
      notifications.show({ color: "red", message: t("settings.usersLoadFailed") });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">{t("settings.usersCount", { n: users.length })}</Text>
        <Button onClick={() => setAddOpen(true)}>{t("settings.addUser")}</Button>
      </Group>

      <Paper withBorder>
        <ResponsiveTable
          mobile={{
            rows: users,
            rowKey: (u) => u.id,
            title: (u) => u.username,
            subtitle: (u) => u.email,
            badge: (u) => (
              u.is_active
                ? <Badge color="green" variant="light">{t("settings.active")}</Badge>
                : <Badge color="gray" variant="light">{t("settings.inactive")}</Badge>
            ),
            fields: [{ label: t("settings.colRole"), render: (u) => <Badge variant="light">{u.role}</Badge> }],
            actions: (u) => (
              <Group gap={4} wrap="nowrap">
                <ActionIcon size="lg" variant="subtle" onClick={() => setEditUser(u)} aria-label="edit">
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon
                  size="lg"
                  variant="subtle"
                  color="red"
                  onClick={() => setDeleteUser(u)}
                  disabled={u.id === currentUser.id}
                  aria-label="delete"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ),
            loading: loading && users.length === 0,
            empty: t("settings.noUsers"),
          }}
        >
        <Table.ScrollContainer minWidth={600}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("settings.colUsername")}</Table.Th>
                <Table.Th>{t("settings.colEmail")}</Table.Th>
                <Table.Th>{t("settings.colRole")}</Table.Th>
                <Table.Th>{t("settings.colActive")}</Table.Th>
                <Table.Th w={100} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && (
                <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">{t("common.loading")}</Text></Table.Td></Table.Tr>
              )}
              {!loading && users.length === 0 && (
                <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">{t("settings.noUsers")}</Text></Table.Td></Table.Tr>
              )}
              {users.map((u) => (
                <Table.Tr key={u.id}>
                  <Table.Td>{u.username}</Table.Td>
                  <Table.Td>{u.email}</Table.Td>
                  <Table.Td><Badge variant="light">{u.role}</Badge></Table.Td>
                  <Table.Td>
                    {u.is_active
                      ? <Badge color="green" variant="light">{t("settings.active")}</Badge>
                      : <Badge color="gray" variant="light">{t("settings.inactive")}</Badge>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <ActionIcon variant="subtle" onClick={() => setEditUser(u)} aria-label="edit">
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => setDeleteUser(u)}
                        disabled={u.id === currentUser.id}
                        aria-label="delete"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        </ResponsiveTable>
      </Paper>

      <AddUserModal opened={addOpen} onClose={() => setAddOpen(false)} onCreated={refresh} />
      <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={refresh} />
      <DeleteUserModal user={deleteUser} onClose={() => setDeleteUser(null)} onDeleted={refresh} />
    </Stack>
  );
}

function AddUserModal({
  opened,
  onClose,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const form = useForm({
    initialValues: { username: "", email: "", password: "", role: "operator" as Role },
    validate: {
      username: (v) => (v.trim().length === 0 ? t("settings.required") : null),
      email: (v) => (v.trim().length === 0 ? t("settings.required") : null),
      password: (v) => (v.length < 8 ? t("settings.pwdMin", { n: 8 }) : null),
    },
  });

  async function submit(values: typeof form.values) {
    try {
      await api.createUser({
        username: values.username.trim(),
        email: values.email.trim(),
        password: values.password,
        role: values.role,
      });
      notifications.show({ color: "green", message: t("settings.userCreated") });
      form.reset();
      onCreated();
      onClose();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      const msg =
        detail && typeof detail === "object" && "error" in detail
          ? String((detail as Record<string, unknown>).error)
          : t("settings.saveFailed");
      notifications.show({ color: "red", message: msg });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("settings.addUser")}>
      <form onSubmit={form.onSubmit(submit)}>
        <Stack>
          <TextInput label={t("settings.colUsername")} required {...form.getInputProps("username")} />
          <TextInput label={t("settings.colEmail")} type="email" required {...form.getInputProps("email")} />
          <PasswordInput label={t("settings.newPasswordLabel")} required {...form.getInputProps("password")} />
          <Select
            label={t("settings.colRole")}
            data={ROLE_OPTIONS}
            allowDeselect={false}
            {...form.getInputProps("role")}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit">{t("common.create")}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: User | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("operator");
  const [active, setActive] = useState(true);
  const [newPwd, setNewPwd] = useState("");

  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setRole(user.role);
      setActive(user.is_active);
      setNewPwd("");
    }
  }, [user]);

  if (!user) return null;

  async function submit() {
    const patch: Parameters<typeof api.updateUser>[1] = {};
    if (email.trim() !== user!.email) patch.email = email.trim();
    if (role !== user!.role) patch.role = role;
    if (active !== user!.is_active) patch.is_active = active;
    if (newPwd) {
      if (newPwd.length < 8) {
        notifications.show({ color: "red", message: t("settings.pwdMin", { n: 8 }) });
        return;
      }
      patch.password = newPwd;
    }
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    try {
      await api.updateUser(user!.id, patch);
      notifications.show({ color: "green", message: t("settings.userSaved") });
      onSaved();
      onClose();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      const msg =
        detail && typeof detail === "object" && "error" in detail
          ? String((detail as Record<string, unknown>).error)
          : t("settings.saveFailed");
      notifications.show({ color: "red", message: msg });
    }
  }

  return (
    <Modal opened={!!user} onClose={onClose} title={`${t("settings.editUser")} — ${user.username}`}>
      <Stack>
        <TextInput label={t("settings.colEmail")} type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
        <Select
          label={t("settings.colRole")}
          data={ROLE_OPTIONS}
          value={role}
          onChange={(v) => v && setRole(v as Role)}
          allowDeselect={false}
        />
        <Switch label={t("settings.activeLabel")} checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />
        <PasswordInput
          label={t("settings.resetPasswordLabel")}
          placeholder={t("settings.resetPasswordPlaceholder")}
          value={newPwd}
          onChange={(e) => setNewPwd(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit}>{t("common.save")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function DeleteUserModal({
  user,
  onClose,
  onDeleted,
}: {
  user: User | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  if (!user) return null;

  async function submit() {
    try {
      await api.deleteUser(user!.id);
      notifications.show({ color: "green", message: t("settings.userDeleted") });
      onDeleted();
      onClose();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      const msg =
        detail && typeof detail === "object" && "error" in detail
          ? String((detail as Record<string, unknown>).error)
          : t("settings.saveFailed");
      notifications.show({ color: "red", message: msg });
    }
  }

  return (
    <Modal opened={!!user} onClose={onClose} title={t("settings.deleteUser")}>
      <Stack>
        <Text>{t("settings.deleteConfirm", { username: user.username })}</Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>{t("common.cancel")}</Button>
          <Button color="red" onClick={submit}>{t("common.delete")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Branding tab ─────────────────────────────────────────────────────────────

function BrandingTab() {
  const { t } = useTranslation();
  const { bumpLogoVersion } = useBranding();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [companyBusy, setCompanyBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.getSetting("branding.company_legal_name");
        if (typeof r.value === "string") setCompanyName(r.value);
      } catch { /* ignore */ }
    })();
  }, []);

  async function saveCompanyName() {
    setCompanyBusy(true);
    try {
      await api.putSetting("branding.company_legal_name", companyName.trim() || null);
      notifications.show({ color: "green", message: t("common.saved") });
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setCompanyBusy(false);
    }
  }

  async function upload() {
    if (!file) return;
    if (file.size > MAX_LOGO_MB * 1024 * 1024) {
      notifications.show({ color: "red", message: t("settings.logoTooBig", { mb: MAX_LOGO_MB }) });
      return;
    }
    setBusy(true);
    try {
      await api.uploadLogo(file);
      bumpLogoVersion();
      setFile(null);
      notifications.show({ color: "green", message: t("settings.logoUploaded") });
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : null;
      const msg =
        detail && typeof detail === "object" && "error" in detail
          ? String((detail as Record<string, unknown>).error)
          : t("settings.saveFailed");
      notifications.show({ color: "red", message: msg });
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setBusy(true);
    try {
      await api.resetLogo();
      bumpLogoVersion();
      notifications.show({ color: "green", message: t("settings.logoReset") });
    } catch {
      notifications.show({ color: "red", message: t("settings.saveFailed") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Group>
          <AppLogo size="lg" />
          <Stack gap={0}>
            <Text fw={500}>{t("settings.currentLogo")}</Text>
            <Text size="xs" c="dimmed">{t("settings.logoHint", { mb: MAX_LOGO_MB })}</Text>
          </Stack>
        </Group>
      </Paper>

      <Paper withBorder p="md">
        <Stack gap="sm">
          <FileInput
            label={t("settings.uploadLogoLabel")}
            placeholder={t("settings.uploadLogoPlaceholder")}
            accept="image/png,image/jpeg,image/svg+xml"
            value={file}
            onChange={setFile}
            leftSection={<IconUpload size={14} />}
          />
          <Group justify="space-between">
            <Button variant="subtle" color="red" leftSection={<IconRefresh size={14} />} onClick={reset} disabled={busy}>
              {t("settings.resetLogo")}
            </Button>
            <Button onClick={upload} disabled={!file || busy} loading={busy}>
              {t("settings.uploadButton")}
            </Button>
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Stack gap="sm">
          <TextInput
            label={t("settings.companyLegalNameLabel")}
            description={t("settings.companyLegalNameHint")}
            placeholder={'„Partener Energo" S.R.L.'}
            value={companyName}
            onChange={(e) => setCompanyName(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button onClick={() => void saveCompanyName()} loading={companyBusy}>
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}

// ── Admin Telegram card ───────────────────────────────────────────────────────

function AdminTelegramCard() {
  const { t } = useTranslation();
  const [token, setToken] = useState("");
  const [username, setUsername] = useState("");
  const [tokenSet, setTokenSet] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const tokState = await api.getSetting("telegram.bot_token");
        setTokenSet(!!tokState?.set);
      } catch { /* ignore */ }
      try {
        const u = await api.getSetting("telegram.bot_username");
        if (typeof u?.value === "string") setUsername(u.value);
      } catch { /* ignore */ }
    })();
  }, []);

  async function saveToken() {
    if (!token) return;
    await api.putSetting("telegram.bot_token", token);
    setToken("");
    setTokenSet(true);
  }

  async function saveUsername() {
    await api.putSetting("telegram.bot_username", username);
  }

  async function test() {
    const res = await api.telegramTest();
    setTestResult(res.ok ? `OK: @${res.info?.username}` : `FAIL: ${res.error}`);
  }

  return (
    <Stack>
      <Group>
        <PasswordInput
          label={t("settings.adminTelegram.tokenLabel")}
          placeholder={tokenSet ? "••••••" : ""}
          value={token}
          onChange={(e) => setToken(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <Button onClick={() => void saveToken()} disabled={!token}>{t("common.save")}</Button>
      </Group>
      <Group>
        <TextInput
          label={t("settings.adminTelegram.usernameLabel")}
          placeholder="my_bot"
          value={username}
          onChange={(e) => setUsername(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <Button onClick={() => void saveUsername()}>{t("common.save")}</Button>
      </Group>
      <Group>
        <Button variant="light" onClick={() => void test()}>{t("settings.adminTelegram.test")}</Button>
        {testResult && <Text size="sm">{testResult}</Text>}
      </Group>
    </Stack>
  );
}

// ── Local agent token card ────────────────────────────────────────────────────

function LocalAgentTokenCard() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ApiTokenStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);

  async function refresh() {
    try {
      setStatus(await api.getApiTokenStatus());
    } catch {
      /* non-fatal */
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  async function handleGenerate() {
    setBusy(true);
    try {
      const r = await api.generateApiToken();
      setIssuedToken(r.token);
      await refresh();
    } catch {
      notifications.show({ color: "red", message: t("settings.apiToken.failed") });
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(t("settings.apiToken.confirmDelete"))) return;
    setBusy(true);
    try {
      await api.deleteApiToken();
      setIssuedToken(null);
      await refresh();
      notifications.show({ color: "green", message: t("settings.apiToken.deleted") });
    } catch {
      notifications.show({ color: "red", message: t("settings.apiToken.failed") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Title order={5}>{t("settings.apiToken.title")}</Title>
        <Text size="sm" c="dimmed">
          {t("settings.apiToken.description")}
        </Text>

        {status?.has_token ? (
          <Group justify="space-between">
            <Text size="sm">
              {t("settings.apiToken.active", {
                date: status.generated_at
                  ? new Date(status.generated_at).toLocaleString()
                  : "—",
              })}
            </Text>
            <Group gap="xs">
              <Button onClick={handleGenerate} loading={busy} variant="light">
                {t("settings.apiToken.regenerate")}
              </Button>
              <Button onClick={handleDelete} color="red" variant="subtle" disabled={busy}>
                {t("common.delete")}
              </Button>
            </Group>
          </Group>
        ) : (
          <Group>
            <Button onClick={handleGenerate} loading={busy}>
              {t("settings.apiToken.generate")}
            </Button>
          </Group>
        )}
      </Stack>

      <Modal
        opened={!!issuedToken}
        onClose={() => setIssuedToken(null)}
        title={t("settings.apiToken.issuedTitle")}
        size="lg"
      >
        <Stack>
          <Text size="sm" c="red">
            {t("settings.apiToken.warningOnce")}
          </Text>
          <Code block fz="md">{issuedToken}</Code>
          <Group justify="flex-end">
            <CopyButton value={issuedToken ?? ""}>
              {({ copied, copy }) => (
                <Button onClick={copy} variant={copied ? "filled" : "light"}>
                  {copied
                    ? t("settings.apiToken.copied")
                    : t("settings.apiToken.copy")}
                </Button>
              )}
            </CopyButton>
            <Button variant="subtle" onClick={() => setIssuedToken(null)}>
              {t("common.close")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
